import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link2, Link2Off, Trash2 } from 'lucide-react';
import { useId, useRef, useState, type SyntheticEvent } from 'react';

import { Button } from '../../components/Button';
import { Field, inputClass } from '../../components/Field';
import { ApiError } from '../../lib/api';
import { useFocusTrap } from '../../lib/useFocusTrap';
import { useAuth } from '../auth/AuthProvider';
import { useLiveStatus, type LiveStatus } from '../events/LiveEvents';
import {
  createWorkstation,
  deleteWorkstation,
  listWorkstations,
  readBinding,
  workstationsKey,
  writeBinding,
  type Binding,
} from './api';

const statusLabel: Record<LiveStatus, string> = {
  off: 'Desconectado',
  connecting: 'Conectando…',
  live: 'Conectado',
  reconnecting: 'Reconectando…',
};

/** Config dialog: create/select a workstation and bind THIS browser to it. */
export function VirtualMatrixDialog({ onClose }: { onClose: () => void }) {
  const { request } = useAuth();
  const qc = useQueryClient();
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const status = useLiveStatus();
  const [binding, setBinding] = useState<Binding | null>(readBinding);
  const [name, setName] = useState('');
  const [error, setError] = useState<string | undefined>();
  useFocusTrap(ref, onClose);

  const list = useQuery({ queryKey: workstationsKey, queryFn: () => listWorkstations(request) });
  // One workstation per operator: hide the create form once they have one.
  const hasOne = (list.data?.length ?? 0) > 0;

  const bind = (b: Binding | null) => {
    writeBinding(b);
    setBinding(b);
  };

  const create = useMutation({
    mutationFn: (n: string) => createWorkstation(request, n),
    onSuccess: async (w) => {
      bind({ id: w.id, name: w.name });
      setName('');
      await qc.invalidateQueries({ queryKey: workstationsKey });
    },
    onError: (err) => {
      setError(
        err instanceof ApiError && err.status === 409
          ? 'Você já tem uma estação. Remova-a antes de criar outra.'
          : 'Não foi possível criar a estação.',
      );
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteWorkstation(request, id),
    onSuccess: async (_d, id) => {
      if (binding?.id === id) bind(null);
      await qc.invalidateQueries({ queryKey: workstationsKey });
    },
    onError: () => {
      setError('Não foi possível excluir a estação.');
    },
  });

  const submit = (e: SyntheticEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Informe um nome.');
      return;
    }
    create.mutate(name.trim());
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center px-4">
      <div aria-hidden="true" className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative w-full max-w-md space-y-4 rounded-lg border border-line-strong bg-surface-raised p-5 shadow-2xl shadow-black/50"
      >
        <h2 id={titleId} className="text-sm font-semibold text-fg">
          Matriz virtual
        </h2>
        <div className="space-y-1 text-[13px]">
          <p className="text-fg">
            {binding ? (
              <>
                Esta tela é: <strong>{binding.name}</strong>
              </>
            ) : (
              'Esta tela não está vinculada.'
            )}
          </p>
          <p className="text-2xs text-fg-subtle">Canal em tempo real: {statusLabel[status]}</p>
          {binding && (
            <Button
              onClick={() => {
                bind(null);
              }}
            >
              <Link2Off aria-hidden="true" className="size-4" />
              Desvincular
            </Button>
          )}
        </div>

        <ul aria-label="Estações" className="max-h-48 space-y-1 overflow-y-auto">
          {(list.data ?? []).map((w) => (
            <li
              key={w.id}
              className="flex items-center justify-between gap-2 rounded-md px-2 py-1.5 text-[13px] text-fg"
            >
              <span className="truncate">
                {w.name}
                {binding?.id === w.id ? ' (esta tela)' : ''}
              </span>
              <span className="flex shrink-0 gap-1">
                <Button
                  aria-label={`Vincular a ${w.name}`}
                  onClick={() => {
                    bind({ id: w.id, name: w.name });
                  }}
                >
                  <Link2 aria-hidden="true" className="size-4" />
                </Button>
                <Button
                  aria-label={`Excluir ${w.name}`}
                  disabled={remove.isPending}
                  onClick={() => {
                    remove.mutate(w.id);
                  }}
                >
                  <Trash2 aria-hidden="true" className="size-4" />
                </Button>
              </span>
            </li>
          ))}
          {list.data?.length === 0 && (
            <li className="px-2 text-[13px] text-fg-subtle">Nenhuma estação cadastrada.</li>
          )}
        </ul>

        {hasOne ? (
          <div className="space-y-3">
            <p className="text-2xs text-fg-subtle">
              Cada operador tem uma estação. Para trocar, remova a atual e crie outra.
            </p>
            <div className="flex justify-end">
              <Button onClick={onClose}>Fechar</Button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <Field label="Nome da estação" error={error}>
              {({ id, describedBy, invalid }) => (
                <input
                  id={id}
                  data-autofocus
                  aria-describedby={describedBy}
                  aria-invalid={invalid}
                  maxLength={80}
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    setError(undefined);
                  }}
                  className={inputClass}
                />
              )}
            </Field>
            <div className="flex justify-end gap-2">
              <Button onClick={onClose}>Fechar</Button>
              <Button type="submit" variant="primary" disabled={create.isPending}>
                {create.isPending ? 'Criando…' : 'Criar e vincular'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
